import { Box, Button, Card, CardContent, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { employeeWalletService, feedback } from '../../../shared/services';
import { dateFormat } from '../../../shared/helpers/dateFormat';
import { IPropsEmployeePage } from '../Employee';

const formatCurrency = (value: number) => `R$ ${value.toFixed(2)}`;

export const EmployeeWallet: React.FC<IPropsEmployeePage> = ({ employee }) => {
  const queryClient = useQueryClient();
  const [openPayoutDialog, setOpenPayoutDialog] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');

  const { data: wallet, isFetching } = useQuery({
    queryKey: ['employeeWallet', employee.id],
    queryFn: () => employeeWalletService.getByEmployeeId(employee.id),
  });

  const { mutate: requestPayout, isLoading: isRequesting } = useMutation(
    (amount: number) => employeeWalletService.requestPayout({
      employeeWalletId: wallet!.id,
      amount,
    }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['employeeWallet', employee.id] });
        setOpenPayoutDialog(false);
        setPayoutAmount('');
        feedback('Solicitação de saque enviada!', 'success');
      },
      onError: (error: Error) => feedback(error.message, 'error'),
    }
  );

  const handleRequestPayout = () => {
    const amount = parseFloat(payoutAmount);
    if (isNaN(amount) || amount <= 0) {
      feedback('Informe um valor válido.', 'error');
      return;
    }
    if (wallet && amount > wallet.pendingCommissions) {
      feedback('Saldo insuficiente.', 'error');
      return;
    }
    requestPayout(amount);
  };

  const statusColor = (status: number) => {
    switch (status) {
    case 0: return 'warning';
    case 1: return 'success';
    case 2: return 'error';
    default: return 'default';
    }
  };

  if (isFetching) {
    return <Box display='flex' justifyContent='center' py={6}><CircularProgress /></Box>;
  }

  return (
    <Box display='flex' flexDirection='column' gap={2}>
      {/* Balance card */}
      <Card>
        <CardContent>
          <Typography variant='h6' gutterBottom>Minha Carteira</Typography>
          <Box display='flex' gap={4} flexWrap='wrap'>
            <Box>
              <Typography variant='caption' color='text.secondary'>Comissões a receber</Typography>
              <Typography variant='h4' color='success.main' fontWeight={700}>
                {formatCurrency(wallet?.pendingCommissions || 0)}
              </Typography>
            </Box>
            <Box>
              <Typography variant='caption' color='text.secondary'>Última atualização</Typography>
              <Typography variant='body2'>
                {wallet?.lastUpdated ? dateFormat(wallet.lastUpdated, 'LLL') : '—'}
              </Typography>
            </Box>
          </Box>

          {wallet && wallet.pendingCommissions > 0 && (
            <Box mt={2}>
              <Button
                variant='contained'
                onClick={() => { setPayoutAmount(wallet?.pendingCommissions?.toString() ?? ''); setOpenPayoutDialog(true); }}
              >
                Solicitar saque
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Payout history */}
      <Card>
        <CardContent>
          <Typography variant='h6' gutterBottom>Histórico de saques</Typography>
          {(!wallet?.payoutRequests || wallet.payoutRequests.length === 0) ? (
            <Typography variant='body2' color='text.secondary'>Nenhum saque solicitado.</Typography>
          ) : (
            <Box display='flex' flexDirection='column' gap={1}>
              {wallet.payoutRequests.map((p) => (
                <Box
                  key={p.id}
                  display='flex'
                  justifyContent='space-between'
                  alignItems='center'
                  p={1.5}
                  sx={{ borderRadius: '8px', backgroundColor: 'action.hover' }}
                >
                  <Box>
                    <Typography variant='body2' fontWeight={600}>{formatCurrency(p.amount)}</Typography>
                    <Typography variant='caption' color='text.secondary'>
                      {dateFormat(p.requestedAt, 'LLL')}
                    </Typography>
                  </Box>
                  <Chip
                    label={p.statusName}
                    color={statusColor(p.status)}
                    size='small'
                  />
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Payout request dialog */}
      <Dialog open={openPayoutDialog} onClose={() => setOpenPayoutDialog(false)}>
        <DialogTitle>Solicitar saque</DialogTitle>
        <DialogContent>
          <Box mt={1}>
            <Typography variant='body2' color='text.secondary' mb={1}>
              Comissões a receber: {formatCurrency(wallet?.pendingCommissions || 0)}
            </Typography>
            <TextField
              label='Valor do saque'
              fullWidth
              placeholder='0,00'
              value={Number(payoutAmount) > 0 ? Number(payoutAmount).toFixed(2) : ''}
              onChange={(e) => {
                const raw = e.target.value.replace(/[^0-9]/g, '');
                setPayoutAmount(raw ? String(parseFloat(raw) / 100) : '');
              }}
              InputProps={{
                startAdornment: <Typography variant='body2' sx={{ mr: 0.5, color: 'text.secondary' }}>R$</Typography>,
              }}
              inputProps={{
                inputMode: 'decimal',
              }}
              autoFocus
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPayoutDialog(false)}>Cancelar</Button>
          <Button
            variant='contained'
            onClick={handleRequestPayout}
            disabled={isRequesting}
          >
            Solicitar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
